// src/components/Chat.js
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Paper, IconButton, TextField, Button, Typography, Tooltip, Badge,
  Divider, Avatar, useMediaQuery, Popover
} from '@mui/material';

import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import CircleRoundedIcon from '@mui/icons-material/CircleRounded';
import NotificationsNoneRoundedIcon from '@mui/icons-material/NotificationsNoneRounded';
import NotificationsActiveRoundedIcon from '@mui/icons-material/NotificationsActiveRounded';
import EmojiEmotionsRoundedIcon from '@mui/icons-material/EmojiEmotionsRounded';
import SmartToyRoundedIcon from '@mui/icons-material/SmartToyRounded';
import { styled } from '@mui/material/styles';
import { AnimatePresence, motion } from 'framer-motion';
import { WalletContext } from './WalletConnect';

// Simple inline crypto token icon (gradient ring + sparkle)
const TokenIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden>
    <defs>
      <linearGradient id="degengrad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#00F0FF" />
        <stop offset="50%" stopColor="#7AFF7A" />
        <stop offset="100%" stopColor="#B388FF" />
      </linearGradient>
    </defs>
    <circle cx="12" cy="12" r="9" fill="none" stroke="url(#degengrad)" strokeWidth="2" />
    <circle cx="12" cy="12" r="5" fill="rgba(255,255,255,0.12)" stroke="rgba(255,255,255,0.22)" />
    <path d="M12 8.5v7M8.5 12h7" stroke="url(#degengrad)" strokeWidth="1.5" strokeLinecap="round" />
    <circle cx="17.5" cy="6.5" r="1.2" fill="url(#degengrad)" />
  </svg>
);

/**
 * Fully functional Global Chat widget wired to Cloudflare Pages + Durable Object.
 *
 * Backend endpoints provided by functions/api/chat/[[path]].ts + GlobalChatRoom DO.
 *
 * Defaults (override with env or props):
 *  WS      → /api/chat/ws?room=<room>
 *  SSE     → /api/chat/sse?room=<room>
 *  POST    → /api/chat/send?room=<room>
 *  HISTORY → /api/chat/history?room=<room>&limit=50
 */

// ------------------------------- Config & helpers --------------------------------
const envWS   = process.env.REACT_APP_CHAT_WS_URL   || process.env.VITE_CHAT_WS_URL   || '';
const envSSE  = process.env.REACT_APP_CHAT_SSE_URL  || process.env.VITE_CHAT_SSE_URL  || '';
const envPOST = process.env.REACT_APP_CHAT_HTTP_URL || process.env.VITE_CHAT_HTTP_URL || '';

const MAX_MESSAGE_LEN = 500;
const MIN_SEND_INTERVAL_MS = 900;
const HISTORY_LIMIT = 80;

const CLIENT_ID = (() => {
  try {
    const k = 'cp:clientId';
    let v = localStorage.getItem(k);
    if (!v) { v = Math.random().toString(36).slice(2); localStorage.setItem(k, v); }
    return v;
  } catch { return Math.random().toString(36).slice(2); }
})();

const makeMsgId = (addr) => `${CLIENT_ID}:${addr || 'anon'}:${Date.now()}:${Math.random().toString(36).slice(2)}`;
const msgKey = (m) => m?.id || `${m?.ts || 0}:${m?.text || m?.body || ''}`;

const hashStr = (s) => {
  let h = 5381; for (let i = 0; i < s.length; i++) h = ((h << 5) + h) ^ s.charCodeAt(i); // djb2 xor
  return Math.abs(h) >>> 0;
};
const shortAddr = (a) => (a ? `${a.slice(0, 6)}…${a.slice(-4)}` : 'Guest');
const suffixFrom = (a) => (hashStr(a).toString(36).slice(-4).toUpperCase());
const now = () => Date.now();

const isCommand = (t) => t.startsWith('/') && t.length > 1;
const parseCommand = (t) => {
  const [cmd, ...rest] = t.slice(1).trim().split(/\s+/);
  return { cmd: (cmd || '').toLowerCase(), arg: rest.join(' ') };
};

// Very small linkifier (http/https only) without injecting HTML
const splitLinks = (text) => {
  const re = /(https?:\/\/[^\s]+)/g;
  const parts = [];
  let lastIndex = 0;
  let m;
  while ((m = re.exec(text)) !== null) {
    if (m.index > lastIndex) parts.push({ t: text.slice(lastIndex, m.index), link: false });
    parts.push({ t: m[0], link: true });
    lastIndex = re.lastIndex;
  }
  if (lastIndex < text.length) parts.push({ t: text.slice(lastIndex), link: false });
  return parts;
};


// ------------------------------- Styled UI --------------------------------
const z = { fab: 1405, panel: 1404 };

const FabWrap = styled(Box)(({ theme }) => ({
  position: 'fixed',
  right: 16, bottom: 16,
  zIndex: z.fab,
  [theme.breakpoints.up('md')]: { right: 24, bottom: 24 },
}));

const FabButton = styled(IconButton)(({ theme }) => ({
  position: 'relative',
  borderRadius: 14,
  padding: '10px 12px',
  border: `1px solid var(--color-border, rgba(255,255,255,0.14))`,
  background: 'var(--color-surfaceAlt, rgba(10,12,16,0.6))',
  backdropFilter: 'blur(12px)',
  boxShadow: '0 8px 28px rgba(0,0,0,0.35)'
    + ', 0 0 0 1px rgba(255,255,255,0.04) inset',
  transition: 'transform 160ms ease, box-shadow 180ms ease',
  '&:hover': {
    transform: 'translateY(-1px) scale(1.02)',
    boxShadow:
      '0 10px 32px rgba(0,0,0,0.45), 0 0 16px rgba(0, 240, 255, 0.18)',
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    inset: -2,
    borderRadius: 16,
    background: 'conic-gradient(from 0deg, #00F0FF, #7AFF7A, #B388FF, #00F0FF)',
    filter: 'blur(8px)',
    opacity: 0.12,
    zIndex: -1,
  },
  '&.unread': {
    boxShadow: '0 12px 36px rgba(0,0,0,0.5), 0 0 24px rgba(0, 240, 255, 0.28)',
  },
}));

const Panel = styled(Paper)(({ theme, full }) => ({
  position: 'fixed',
  right: full ? 0 : 12,
  bottom: full ? 0 : 72,
  top: full ? 0 : 'auto',
  left: full ? 0 : 'auto',
  width: full ? '100%' : 380,
  maxWidth: '96vw',
  height: full ? '100dvh' : 520,
  maxHeight: full ? '100dvh' : '78vh',
  minHeight: 0,
  zIndex: z.panel,
  borderRadius: full ? '18px 18px 0 0' : 16,
  border: `1px solid var(--color-border, rgba(255,255,255,0.14))`,
  background: 'linear-gradient(180deg, rgba(14,17,23,0.9), rgba(10,12,16,0.84))',
  backdropFilter: 'blur(14px)',
  overflow: 'hidden',
  display: 'grid',
  gridTemplateRows: 'auto 1fr auto',
  [theme.breakpoints.up('md')]: {
    height: full ? '70vh' : 560,
  },
}));

const Header = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: '10px 12px',
  borderBottom: '1px solid var(--color-border, rgba(255,255,255,0.12))',
}));

const StatusDot = ({ ok }) => (
  <CircleRoundedIcon
    fontSize="inherit"
    style={{ fontSize: 10, color: ok ? 'var(--color-success, #21D07A)' : 'var(--color-warning, #F0B90B)' }}
  />
);

const Messages = styled(Box)(() => ({
  overflowY: 'auto',
  WebkitOverflowScrolling: 'touch',
  padding: '10px 10px 6px',
  '@media (max-width:600px)': { padding: '8px 8px 4px' },
  minHeight: 0,
  height: '100%',
  overscrollBehavior: 'contain',
  touchAction: 'pan-y',
}));

const Bubble = styled(Box)(({ mine }) => ({
  alignSelf: mine ? 'flex-end' : 'flex-start',
  maxWidth: '85%',
  padding: '8px 10px',
  borderRadius: 12,
  margin: '4px 0',
  fontSize: 14,
  lineHeight: 1.3,
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
  background: mine
    ? 'linear-gradient(135deg, rgba(33,208,122,0.15), rgba(33,208,122,0.10))'
    : 'rgba(255,255,255,0.06)',
  border: `1px solid var(--color-border, rgba(255,255,255,0.12))`,
}));

const InputBar = styled(Box)(() => ({
  display: 'flex',
  gap: 8,
  padding: 10,
  borderTop: '1px solid var(--color-border, rgba(255,255,255,0.12))',
  background: 'rgba(0,0,0,0.15)',
  paddingBottom: 'calc(10px + env(safe-area-inset-bottom))',
}));

// ------------------------------- Transports --------------------------------
const useChatTransport = ({ room, onMessage, urls }) => {
  const wsRef = useRef(null);
  const esRef = useRef(null);
  const bcRef = useRef(null);
  const [state, setState] = useState({ mode: 'idle', connected: false }); // mode: ws|sse|broadcast|idle
  const retryRef = useRef({ tries: 0, timer: null });
  const guardRef = useRef(false);

  const clearRetry = useCallback(() => {
    const t = retryRef.current.timer; if (t) clearTimeout(t); retryRef.current.timer = null;
  }, []);
  const scheduleRetry = useCallback((fn) => {
    const tries = ++retryRef.current.tries;
    const delay = Math.min(15000, 400 * Math.pow(1.8, tries));
    clearRetry();
    retryRef.current.timer = setTimeout(() => fn(), delay);
  }, [clearRetry]);

  const connect = useCallback(() => {
    let cancelled = false;
    let triedSSE = false; // immediate fallback flag

    const connectWS = () => new Promise((resolve) => {
      const raw = urls.ws || `/api/chat/ws?room=${encodeURIComponent(room)}`;
      let url = raw;
      try {
        if (raw.startsWith('http://') || raw.startsWith('https://')) {
          url = raw.replace(/^http/i, 'ws');
        } else if (raw.startsWith('/')) {
          const proto = (typeof window !== 'undefined' && window.location?.protocol === 'https:') ? 'wss:' : 'ws:';
          const host = (typeof window !== 'undefined' && window.location?.host) || '';
          url = `${proto}//${host}${raw}`;
        } // else assume ws:// or wss:// is already provided
        const ws = new WebSocket(url);
        wsRef.current = ws;
        ws.addEventListener('open', () => {
          if (cancelled) return;
          triedSSE = false; // reset once WS opens
          retryRef.current.tries = 0; // reset backoff
          setState({ mode: 'ws', connected: true });
          resolve(true);
        });
        ws.addEventListener('message', (ev) => {
          if (cancelled) return;
          try { const data = JSON.parse(ev.data); onMessage?.(data); } catch {}
        });
        ws.addEventListener('close', () => {
          if (cancelled) return;
          setState((s) => ({ ...s, connected: false }));
          // Immediate SSE fallback once, then retry WS with backoff
          if (!triedSSE) {
            triedSSE = true;
            connectSSE().then((ok) => {
              if (!ok) scheduleRetry(() => connectWS());
            });
          } else {
            scheduleRetry(() => connectWS());
          }
        });
        ws.addEventListener('error', () => {
          if (cancelled) return;
          if (!triedSSE) {
            triedSSE = true;
            connectSSE().then((ok) => {
              if (!ok) scheduleRetry(() => connectWS());
            });
          }
        });
      } catch { resolve(false); }
    });

    const connectSSE = () => new Promise((resolve) => {
      const url = urls.sse || `/api/chat/sse?room=${encodeURIComponent(room)}`;
      try {
        const es = new EventSource(url, { withCredentials: false });
        esRef.current = es;
        let opened = false;
        es.onopen = () => {
          if (cancelled) return;
          opened = true;
          retryRef.current.tries = 0;
          setState({ mode: 'sse', connected: true });
          resolve(true);
        };
        es.onmessage = (ev) => {
          if (cancelled) return;
          try { const data = JSON.parse(ev.data); onMessage?.(data); } catch {}
        };
        es.onerror = () => {
          if (!opened) resolve(false);
          setState((s) => ({ ...s, connected: false }));
          scheduleRetry(() => connectSSE());
        };
      } catch { resolve(false); }
    });

    const connectBC = () => {
      try {
        const bc = new BroadcastChannel('cp-global-chat');
        bcRef.current = bc;
        bc.onmessage = (ev) => onMessage?.(ev.data);
        setState({ mode: 'broadcast', connected: true });
        return true;
      } catch {
        setState({ mode: 'idle', connected: false });
        return false;
      }
    };

    (async () => {
      const okWS = await connectWS();
      if (!okWS) {
        const okSSE = await connectSSE();
        if (!okSSE) connectBC();
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [room, onMessage, urls, scheduleRetry]);

  useEffect(() => {
    if (guardRef.current) return; // prevent double connect under StrictMode
    guardRef.current = true;
    const cancel = connect();
    return () => {
      cancel?.();
      guardRef.current = false;
      clearRetry();
      try { wsRef.current?.close?.(); } catch {}
      try { esRef.current?.close?.(); } catch {}
      try { bcRef.current?.close?.(); } catch {}
    };
  }, [connect, clearRetry]);

  const send = useCallback(async (payload) => {
    // Try WS first
    try {
      if (state.mode === 'ws' && state.connected && wsRef.current?.readyState === 1) {
        wsRef.current.send(JSON.stringify(payload));
        return { ok: true, mode: 'ws' };
      }
    } catch {}
    // SSE/WS fallback via POST
    try {
      const url = urls.post || `/api/chat/send?room=${encodeURIComponent(room)}`;
      const r = await fetch(url, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(payload),
      });
      return { ok: r.ok, mode: 'http', status: r.status };
    } catch {}
    // BroadcastChannel demo fallback
    try {
      if (bcRef.current) {
        bcRef.current.postMessage(payload);
        return { ok: true, mode: 'broadcast' };
      }
    } catch {}
    return { ok: false };
  }, [state, urls, room]);

  return { state, send };
};

// ------------------------------- Main component --------------------------------
export default function GlobalChat({ room = 'global', wsUrl = envWS, sseUrl = envSSE, postUrl = envPOST, docked = false, style }) {
  const { address } = useContext(WalletContext);
  const mobile = useMediaQuery('(max-width:600px)');
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const [open, setOpen] = useState(() => {
    if (docked) return true;
    try { return localStorage.getItem('cp:chatOpen') === '1'; } catch { return false; }
  });
  const [unread, setUnread] = useState(0);
  const draftKey = useMemo(() => `cp:chatDraft:${room}`, [room]);
  const [input, setInput] = useState(() => {
    try { return localStorage.getItem(draftKey) || ''; } catch { return ''; }
  });
  const [messages, setMessages] = useState([]);
  const [sending, setSending] = useState(false);
  const lastSendRef = useRef(0);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const loadedHistoryRef = useRef(false);
  const seenRef = useRef(new Set());
  const seenConnectRef = useRef(new Set()); // e.g. 'connected:global'
  const [atBottom, setAtBottom] = useState(true);
  const [pending, setPending] = useState(0); // new messages while scrolled up
  const [emojiAnchor, setEmojiAnchor] = useState(null);
  const EMOJIS = useMemo(() => [
    '😀','🤣','😎','🔥','💎','🚀','🤝','🎯','💰','🧠','🫡','🏦','🎲','🎰','⚡️','❤️','👍','👎','❗️','❓'
  ], []);

  const [notify, setNotify] = useState(() => {
    try { return localStorage.getItem('cp:notify') === '1'; } catch { return false; }
  });
  const [showDemo, setShowDemo] = useState(() => {
    try { return localStorage.getItem('cp:showDemo') !== '0'; } catch { return true; }
  });
  const canNotify = typeof window !== 'undefined' && 'Notification' in window;
  const requestNotify = useCallback(async () => {
    if (!canNotify) return false;
    if (Notification.permission === 'granted') return true;
    if (Notification.permission === 'denied') return false;
    try {
      const res = await Notification.requestPermission();
      return res === 'granted';
    } catch { return false; }
  }, [canNotify]);

  // Route localhost dev → prod API to avoid 404/CORS during local dev
  const urls = useMemo(() => {
    const DEV_BASE =
      (typeof window !== 'undefined' &&
        /^(http:\/\/localhost|http:\/\/127\.0\.0\.1)/.test(window.location.origin))
        ? 'https://chainplay.bet'
        : '';
    return {
      ws:  wsUrl  || `${DEV_BASE}/api/chat/ws?room=${encodeURIComponent(room)}`,
      sse: sseUrl || `${DEV_BASE}/api/chat/sse?room=${encodeURIComponent(room)}`,
      post: postUrl || `${DEV_BASE}/api/chat/send?room=${encodeURIComponent(room)}`,
      history:       `${DEV_BASE}/api/chat/history?room=${encodeURIComponent(room)}&limit=${HISTORY_LIMIT}`,
    };
  }, [room, wsUrl, sseUrl, postUrl]);

  // identity
  const handle = useMemo(() => {
    const base = address ? `${shortAddr(address)}#${suffixFrom(address)}` : (() => {
      const k = 'cp:guestId';
      try {
        const saved = localStorage.getItem(k);
        if (saved) return saved;
        const g = `Guest#${suffixFrom(`${Math.random()}`)}`;
        localStorage.setItem(k, g);
        return g;
      } catch { return `Guest#${suffixFrom(`${Math.random()}`)}`; }
    })();
    return base;
  }, [address]);

  // Seed a system notice once
  useEffect(() => {
    if (messages.length) return;
    setMessages([{
      id: `sys-${Date.now()}`,
      type: 'sys',
      text: `Welcome to Global Chat (#${room}). Be respectful. No spam.`,
      ts: now(),
    }]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch history once when panel opens the first time
  useEffect(() => {
    if (!open || loadedHistoryRef.current) return;
    (async () => {
      let ok = false;
      try {
        const r = await fetch(urls.history);
        if (!r.ok) throw new Error(`history ${r.status}`);
        const j = await r.json();

        // Accept multiple server shapes: {messages: [...]}, {items: [...]}, {data: [...]}
        const items = Array.isArray(j.messages)
          ? j.messages
          : Array.isArray(j.items)
          ? j.items
          : Array.isArray(j.data)
          ? j.data
          : [];

        const normalized = items
          .map((m) => ({
            id: m.id || `${m.room || room}:${m.ts}`,
            type: m.type || 'msg',
            text: m.body || m.text || '',
            from: m.from || (m.wallet ? shortAddr(m.wallet) : 'anon'),
            addr: m.wallet || null,
            ts: m.ts || Date.now(),
          }))
          .reverse();

        normalized.forEach((m) => seenRef.current.add(msgKey(m)));
        setMessages((prev) => [...prev, ...normalized].slice(-400));
        ok = true;
      } catch (e) {
        // keep panel usable; will try again next open
      } finally {
        if (ok) loadedHistoryRef.current = true;
      }
    })();
  }, [open, urls.history, room]);

  // Transport (WS → SSE → BC)
  const onMessage = useCallback((msg) => {
    if (!msg || typeof msg !== 'object') return;
    const type = msg.type || 'msg';

    // Respect "Show demo" for live bot lines
    if (type === 'bot' && !showDemo) return;

    const text = msg.text || msg.body || '';
    const from = msg.from || (msg.wallet ? shortAddr(msg.wallet) : 'anon');
    const addr = msg.addr || msg.wallet || null;
    const ts = Number(msg.ts || Date.now());

    const key = msgKey({ id: msg.id, ts, text });
    if (seenRef.current.has(key)) return;
    seenRef.current.add(key);

    // Append msg/bot/emote to the timeline
    if ((type === 'msg' || type === 'bot' || type === 'emote') && text) {
      setMessages((prev) => {
        const next = [...prev, { type, text, from, addr, ts, id: msg.id }].slice(-400);
        if (!open) setUnread((u) => Math.min(999, u + 1));
        else if (!atBottom) setPending((p) => Math.min(999, p + 1));
        return next;
      });
      // Notifications only for real user messages
      try {
        if (type === 'msg' && !open && notify && canNotify && document.hidden && Notification.permission === 'granted') {
          const body = `${from || 'anon'}: ${text}`.slice(0, 90);
          new Notification('Degen Chat', { body });
        }
      } catch {}
    } else if (type === 'sys' && text) {
      // Deduplicate noisy connects: only show the first "connected:room" per session
      if (text.startsWith('connected:')) {
        if (seenConnectRef.current.has(text)) return;
        seenConnectRef.current.add(text);
      }
      setMessages((prev) => [...prev, { type: 'sys', text, ts, id: msg.id }].slice(-400));
    }
  }, [open, atBottom, notify, canNotify, showDemo]);

  const { state, send } = useChatTransport({ room, onMessage, urls });

  // Smart autoscroll: only scroll if at bottom
  useEffect(() => {
    if (!open) return;
    const el = scrollRef.current;
    if (!el) return;
    if (atBottom) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  }, [messages, open, atBottom]);

  // Scroll handler for atBottom/pending
  const onScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const threshold = 60; // px from bottom counts as bottom
    const isBottom = el.scrollHeight - el.scrollTop - el.clientHeight < threshold;
    setAtBottom(isBottom);
    if (isBottom && pending) setPending(0);
  }, [pending]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    onScroll();
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [onScroll]);

  // Persist open state (not in docked mode)
  useEffect(() => {
    if (docked) return;
    try { localStorage.setItem('cp:chatOpen', open ? '1' : '0'); } catch {}
  }, [open, docked]);

  // Reset unread when opened
  useEffect(() => { if (open) setUnread(0); }, [open]);

  const canSend = useMemo(() => {
    const t = input.trim();
    return t.length > 0 && t.length <= MAX_MESSAGE_LEN && !sending;
  }, [input, sending]);

  const doSend = useCallback(async () => {
    const t = input.trim();
    if (!t) return;
    if (t.length > MAX_MESSAGE_LEN) return;

    // Slash command handling
    if (isCommand(t)) {
      const { cmd, arg } = parseCommand(t);
      if (cmd === 'clear') {
        setMessages([]);
        try { localStorage.removeItem(draftKey); } catch {}
        setInput('');
        return;
      }
      if (cmd === 'help') {
        setMessages((prev) => [...prev, {
          id: `sys-${Date.now()}`,
          type: 'sys',
          text: 'Commands: /help · /clear · /me <action>',
          ts: now(),
        }].slice(-400));
        setInput('');
        return;
      }
      if (cmd === 'me' && arg) {
        // send as emote (client formats italic)
        const id = makeMsgId(address);
        const payload = { id, type: 'emote', text: arg, from: handle, addr: address || null, ts: now() };
        seenRef.current.add(msgKey(payload));
        setMessages((prev) => [...prev, { ...payload, _local: true }].slice(-400));
        try { await send(payload); } catch {}
        setInput('');
        try { localStorage.removeItem(draftKey); } catch {}
        return;
      }
      // unknown commands: drop into normal send
    }

    if (Date.now() - lastSendRef.current < MIN_SEND_INTERVAL_MS) return;
    lastSendRef.current = Date.now();

    setSending(true);
    const id = makeMsgId(address);
    const payload = {
      id,
      type: 'msg',
      text: t,
      from: handle,
      addr: address || null,
      ts: now(),
    };

    seenRef.current.add(msgKey(payload));
    setMessages((prev) => [...prev, { ...payload, _local: true }].slice(-400));

    try {
      const res = await send(payload);
      if (!res.ok) {
        setMessages((prev) => [...prev, {
          id: `sys-${Date.now()}`,
          type: 'sys',
          text: 'Message failed to send.',
          ts: now(),
        }]);
      }
    } finally {
      setSending(false);
      setInput('');
      try { localStorage.removeItem(draftKey); } catch {}
    }
  }, [input, handle, address, send, draftKey]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (canSend) doSend();
      return;
    }
    const mod = e.ctrlKey || e.metaKey;
    if (mod && (e.key === 'e' || e.key === 'E')) {
      e.preventDefault();
      setEmojiAnchor(e.currentTarget || inputRef.current);
      return;
    }
  };

  const insertAtCursor = useCallback((el, insert) => {
    if (!el) return;
    const start = el.selectionStart ?? el.value.length;
    const end = el.selectionEnd ?? el.value.length;
    const next = (input.slice(0, start) + insert + input.slice(end)).slice(0, MAX_MESSAGE_LEN);
    setInput(next);
    try { localStorage.setItem(draftKey, next); } catch {}
    // restore caret after inserted emoji
    requestAnimationFrame(() => {
      try {
        el.focus();
        const pos = start + insert.length;
        el.setSelectionRange(pos, pos);
      } catch {}
    });
  }, [input, draftKey]);

  const full = mobile;

  useEffect(() => {
    const handler = (e) => {
      const mod = e.ctrlKey || e.metaKey;
      if (e.key === 'Escape' && open) {
        e.preventDefault();
        setOpen(false);
        return;
      }
      if (mod && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open]);

  useEffect(() => {
    if (open) {
      // Delay to ensure panel mounts before focus
      const t = setTimeout(() => { try { inputRef.current?.focus(); } catch {} }, 10);
      return () => clearTimeout(t);
    }
  }, [open]);

  const timeline = useMemo(() => {
    const out = [];
    let lastDay = '';
    for (const m of messages) {
      const ts = new Date(m.ts || Date.now());
      const day = ts.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
      if (day !== lastDay) {
        out.push({ type: 'sep', key: `sep-${day}-${ts.getTime()}`, day });
        lastDay = day;
      }
      out.push(m);
    }
    return out;
  }, [messages]);

  // ------------------------------ Render --------------------------------

  return (
    <>
      {/* Floating FAB (hidden in docked mode and while panel is open) */}
      {!docked && !open && (
        <FabWrap>
          <Tooltip
            title={open ? 'Close Degen Chat' : 'Open Degen Chat'}
            placement="left"
            arrow
          >
            <motion.div
              animate={!prefersReducedMotion && unread ? { scale: [1, 1.06, 1] } : { scale: 1 }}
              transition={!prefersReducedMotion && unread ? { duration: 1.6, repeat: Infinity } : {}}
              style={{ borderRadius: 18 }}
            >
              <Badge
                color="primary"
                badgeContent={unread || 0}
                invisible={!unread}
                overlap="circular"
              >
                <FabButton
                  onClick={() => setOpen((v) => !v)}
                  aria-label="Open Degen Chat"
                  className={unread ? 'unread' : ''}
                >
                  <TokenIcon size={18} />
                  <Typography
                    component="span"
                    sx={{ ml: 1, fontWeight: 800, fontSize: 12, letterSpacing: 0.3, display: 'inline', whiteSpace: 'nowrap' }}
                  >
                    Degen Chat
                  </Typography>
                </FabButton>
              </Badge>
            </motion.div>
          </Tooltip>
        </FabWrap>
      )}

      {/* Docked (right panel): inline, fills parent */}
      {docked && (
        <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', height: '100%', minHeight: 0, ...(style || {}) }}>
          {/* Header */}
          <Header>
            <Avatar
              sx={{
                width: 28, height: 28, fontSize: 12, bgcolor: 'rgba(255,255,255,0.10)',
                border: '1px solid var(--color-border, rgba(255,255,255,0.14))',
                display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}
            >
              <TokenIcon size={18} />
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: 13,
                  fontWeight: 900,
                  background: 'linear-gradient(90deg, #00F0FF, #7AFF7A)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  letterSpacing: 0.4,
                }}
              >
                Degen Chat
              </Typography>
              <Box sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                px: 0.75,
                py: 0.25,
                borderRadius: 999,
                border: '1px solid rgba(255,255,255,0.14)',
                background: 'rgba(255,255,255,0.05)'
              }}>
                <StatusDot ok={state.connected} />
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {state.connected ? (state.mode === 'ws' ? 'WebSocket' : state.mode === 'sse' ? 'SSE' : 'Local') : 'Connecting…'}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ flex: 1 }} />
            {canNotify && (
              <Tooltip title={notify ? 'Notifications on' : 'Notifications off'} arrow>
                <IconButton
                  size="small"
                  onClick={async () => {
                    if (!notify) {
                      const ok = await requestNotify();
                      if (!ok) return;
                    }
                    setNotify((v) => {
                      const nv = !v;
                      try { localStorage.setItem('cp:notify', nv ? '1' : '0'); } catch {}
                      return nv;
                    });
                  }}
                  sx={{ color: notify ? 'var(--color-success, #21D07A)' : 'text.secondary' }}
                >
                  {notify ? <NotificationsActiveRoundedIcon fontSize="small" /> : <NotificationsNoneRoundedIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title={showDemo ? 'Hide demo chatters' : 'Show demo chatters'} arrow>
              <IconButton
                size="small"
                onClick={() => {
                  setShowDemo(v => {
                    const nv = !v;
                    try { localStorage.setItem('cp:showDemo', nv ? '1' : '0'); } catch {}
                    return nv;
                  });
                }}
                sx={{ color: showDemo ? 'text.secondary' : 'rgba(255,255,255,0.45)' }}
                aria-label={showDemo ? 'Hide demo messages' : 'Show demo messages'}
              >
                <SmartToyRoundedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Header>

          {/* Messages */}
          <Box sx={{ position: 'relative', minHeight: 0, flex: 1, display: 'flex', flexDirection: 'column' }}>
            <Messages ref={scrollRef} role="log" aria-live="polite" aria-relevant="additions text" sx={{ flex: 1 }}>
              {timeline.map((m, i) => {
                if (m.type === 'bot') {
                  if (!showDemo) return null; // respect toggle for history too
                  const text = m.text || m.body || '';
                  return (
                    <Box key={`${m.ts}-${i}`} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                      <Typography variant="caption" sx={{ color: 'text.secondary', px: 0.5, mb: 0.25 }}>
                        {m.from}
                        <span style={{ opacity: 0.6 }}> • {new Date(m.ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                      </Typography>
                      <Bubble mine={false} title={new Date(m.ts).toISOString()}>
                        {splitLinks(text).map((p, idx) =>
                          p.link ? (
                            <a key={idx} href={p.t} target="_blank" rel="noreferrer" style={{ color: 'var(--color-primary)', textDecoration: 'underline' }}>
                              {p.t}
                            </a>
                          ) : (
                            <span key={idx}>{p.t}</span>
                          )
                        )}
                      </Bubble>
                    </Box>
                  );
                }
                if (m.type === 'sep') {
                  return (
                    <Box key={m.key} sx={{ display: 'flex', alignItems: 'center', my: 0.75, opacity: 0.6 }}>
                      <Box sx={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.1)' }} />
                      <Typography variant="caption" sx={{ px: 1 }}>{m.day}</Typography>
                      <Box sx={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.1)' }} />
                    </Box>
                  );
                }
                if (m.type === 'emote') {
                  return (
                    <Box key={`emote-${m.ts}-${i}`} sx={{ display: 'flex', justifyContent: 'center', my: 0.5 }}>
                      <Typography variant="caption" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                        * {m.from} {m.text}
                      </Typography>
                    </Box>
                  );
                }
                if (m.type === 'sys') {
                  return (
                    <Box key={`sys-${m.ts}-${i}`} sx={{ display: 'flex', justifyContent: 'center', my: 0.5 }}>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        {new Date(m.ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} — {m.text}
                      </Typography>
                    </Box>
                  );
                }
                const mine = (address && m.addr && address.toLowerCase() === String(m.addr).toLowerCase()) || m.from === handle;
                const text = m.text || m.body || '';
                return (
                  <Box key={`${m.ts}-${i}`} sx={{ display: 'flex', flexDirection: 'column', alignItems: mine ? 'flex-end' : 'flex-start' }}>
                    <Typography variant="caption" sx={{ color: 'text.secondary', px: 0.5, mb: 0.25 }}>
                      {m.from}
                      <span style={{ opacity: 0.6 }}> • {new Date(m.ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                    </Typography>
                    <Bubble mine={mine} title={new Date(m.ts).toISOString()}>
                      {splitLinks(text).map((p, idx) =>
                        p.link ? (
                          <a key={idx} href={p.t} target="_blank" rel="noreferrer" style={{ color: 'var(--color-primary)', textDecoration: 'underline' }}>
                            {p.t}
                          </a>
                        ) : (
                          <span key={idx}>
                            {p.t.split(handle).reduce((acc, part, i2) => {
                              if (i2 === 0) return [part];
                              return acc.concat([<strong key={`hl-${i2}`}>{handle}</strong>, part]);
                            }, [])}
                          </span>
                        )
                      )}
                    </Bubble>
                  </Box>
                );
              })}
            </Messages>
            {pending > 0 && (
              <Box sx={{ position: 'absolute', bottom: 56, left: 0, right: 0, display: 'flex', justifyContent: 'center' }}>
                <Button
                  size="small"
                  onClick={() => {
                    const el = scrollRef.current; if (!el) return;
                    el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
                    setPending(0); setAtBottom(true);
                  }}
                  sx={{
                    borderRadius: 999,
                    px: 1.25,
                    minHeight: 28,
                    background: 'rgba(0,0,0,0.4)',
                    border: '1px solid rgba(255,255,255,0.18)',
                    backdropFilter: 'blur(8px)',
                    textTransform: 'none',
                    fontWeight: 800,
                  }}
                >
                  ▼ {pending} new message{pending > 1 ? 's' : ''}
                </Button>
              </Box>
            )}
          </Box>

          {/* Input */}
          <Divider sx={{ opacity: 0.12 }} />
          <InputBar>
            <TextField
              fullWidth
              size="small"
              placeholder={`Message as ${handle} · Enter to send`}
              value={input}
              onChange={(e) => {
                const v = e.target.value.slice(0, MAX_MESSAGE_LEN);
                setInput(v);
                try { localStorage.setItem(draftKey, v); } catch {}
              }}
              onKeyDown={handleKeyDown}
              inputProps={{ maxLength: MAX_MESSAGE_LEN }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  background: 'rgba(255,255,255,0.05)',
                  borderRadius: 'var(--radius-button, 10px)',
                  fontWeight: 600,
                },
              }}
              aria-label="Chat message input"
              inputRef={inputRef}
            />
            <Tooltip title="Emojis" arrow>
              <IconButton
                size="small"
                onClick={(e) => setEmojiAnchor(e.currentTarget)}
                sx={{ border: '1px solid rgba(255,255,255,0.14)' }}
                aria-label="Open emoji picker"
              >
                <EmojiEmotionsRoundedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Popover
              open={!!emojiAnchor}
              anchorEl={emojiAnchor}
              onClose={() => setEmojiAnchor(null)}
              anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
              transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
              PaperProps={{
                sx: {
                  p: 1,
                  borderRadius: 2,
                  bgcolor: 'rgba(10,12,16,0.9)',
                  border: '1px solid rgba(255,255,255,0.14)'
                }
              }}
            >
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(8, 1fr)', gap: 0.5 }}>
                {EMOJIS.map((emo) => (
                  <Button
                    key={emo}
                    onClick={() => {
                      setEmojiAnchor(null);
                      insertAtCursor(inputRef.current, emo);
                    }}
                    sx={{ minWidth: 0, p: 0.5, fontSize: 18, lineHeight: 1 }}
                    title={emo}
                  >
                    {emo}
                  </Button>
                ))}
              </Box>
            </Popover>
            <Button
              component={motion.button}
              whileTap={prefersReducedMotion ? undefined : { scale: 0.98 }}
              whileHover={prefersReducedMotion ? undefined : { scale: 1.02 }}
              title="Send (Enter)"
              variant="contained"
              onClick={doSend}
              disabled={!canSend}
              startIcon={<SendRoundedIcon />}
              sx={{
                borderRadius: 'var(--radius-button, 10px)',
                fontWeight: 900,
                textTransform: 'none',
                backgroundImage: 'var(--grad-primary)',
                color: 'var(--button-label-primary, #FFF)',
                px: 1.5,
              }}
            >
              Send
            </Button>
          </InputBar>
        </Box>
      )}

      {/* Floating overlay panel (mobile / non-docked) */}
      <AnimatePresence>
        {!docked && open && (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 16 }}
            transition={prefersReducedMotion ? { duration: 0 } : { duration: 0.18, ease: 'easeOut' }}
            style={{ position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: z.panel }}
          >
            <Panel
              component={motion.div}
              full={full ? 1 : 0}
              style={{ pointerEvents: 'auto', margin: full ? 0 : undefined }}
              initial={{ scale: 0.98 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.98 }}
              transition={{ duration: 0.18 }}
              elevation={0}
            >
              {/* Header */}
              <Header>
                <Avatar
                  sx={{
                    width: 28, height: 28, fontSize: 12, bgcolor: 'rgba(255,255,255,0.10)',
                    border: '1px solid var(--color-border, rgba(255,255,255,0.14))',
                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                  }}
                >
                  <TokenIcon size={18} />
                </Avatar>
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontSize: 13,
                      fontWeight: 900,
                      background: 'linear-gradient(90deg, #00F0FF, #7AFF7A)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      letterSpacing: 0.4,
                    }}
                  >
                    Degen Chat
                  </Typography>
                  <Box sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                    px: 0.75,
                    py: 0.25,
                    borderRadius: 999,
                    border: '1px solid rgba(255,255,255,0.14)',
                    background: 'rgba(255,255,255,0.05)'
                  }}>
                    <StatusDot ok={state.connected} />
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      {state.connected ? (state.mode === 'ws' ? 'WebSocket' : state.mode === 'sse' ? 'SSE' : 'Local') : 'Connecting…'}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ flex: 1 }} />
                {canNotify && (
                  <Tooltip title={notify ? 'Notifications on' : 'Notifications off'} arrow>
                    <IconButton
                      size="small"
                      onClick={async () => {
                        if (!notify) {
                          const ok = await requestNotify();
                          if (!ok) return;
                        }
                        setNotify((v) => {
                          const nv = !v;
                          try { localStorage.setItem('cp:notify', nv ? '1' : '0'); } catch {}
                          return nv;
                        });
                      }}
                      sx={{ color: notify ? 'var(--color-success, #21D07A)' : 'text.secondary' }}
                    >
                      {notify ? <NotificationsActiveRoundedIcon fontSize="small" /> : <NotificationsNoneRoundedIcon fontSize="small" />}
                    </IconButton>
                  </Tooltip>
                )}
                <Tooltip title={showDemo ? 'Hide demo chatters' : 'Show demo chatters'} arrow>
                  <IconButton
                    size="small"
                    onClick={() => {
                      setShowDemo(v => {
                        const nv = !v;
                        try { localStorage.setItem('cp:showDemo', nv ? '1' : '0'); } catch {}
                        return nv;
                      });
                    }}
                    sx={{ color: showDemo ? 'text.secondary' : 'rgba(255,255,255,0.45)' }}
                    aria-label={showDemo ? 'Hide demo messages' : 'Show demo messages'}
                  >
                    <SmartToyRoundedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Close" arrow>
                  <IconButton size="small" onClick={() => setOpen(false)} sx={{ color: 'text.secondary' }}>
                    <CloseRoundedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Header>

              {/* Messages */}
              <Box sx={{ position: 'relative', minHeight: 0 }}>
                <Messages ref={scrollRef} role="log" aria-live="polite" aria-relevant="additions text">
                  {timeline.map((m, i) => {
                    if (m.type === 'bot') {
                      if (!showDemo) return null; // respect toggle for history too
                      const text = m.text || m.body || '';
                      return (
                        <Box key={`${m.ts}-${i}`} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                          <Typography variant="caption" sx={{ color: 'text.secondary', px: 0.5, mb: 0.25 }}>
                            {m.from}
                            <span style={{ opacity: 0.6 }}> • {new Date(m.ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                          </Typography>
                          <Bubble mine={false} title={new Date(m.ts).toISOString()}>
                            {splitLinks(text).map((p, idx) =>
                              p.link ? (
                                <a key={idx} href={p.t} target="_blank" rel="noreferrer" style={{ color: 'var(--color-primary)', textDecoration: 'underline' }}>
                                  {p.t}
                                </a>
                              ) : (
                                <span key={idx}>{p.t}</span>
                              )
                            )}
                          </Bubble>
                        </Box>
                      );
                    }
                    if (m.type === 'sep') {
                      return (
                        <Box key={m.key} sx={{ display: 'flex', alignItems: 'center', my: 0.75, opacity: 0.6 }}>
                          <Box sx={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.1)' }} />
                          <Typography variant="caption" sx={{ px: 1 }}>{m.day}</Typography>
                          <Box sx={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.1)' }} />
                        </Box>
                      );
                    }
                    if (m.type === 'emote') {
                      return (
                        <Box key={`emote-${m.ts}-${i}`} sx={{ display: 'flex', justifyContent: 'center', my: 0.5 }}>
                          <Typography variant="caption" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                            * {m.from} {m.text}
                          </Typography>
                        </Box>
                      );
                    }
                    if (m.type === 'sys') {
                      return (
                        <Box key={`sys-${m.ts}-${i}`} sx={{ display: 'flex', justifyContent: 'center', my: 0.5 }}>
                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            {new Date(m.ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} — {m.text}
                          </Typography>
                        </Box>
                      );
                    }
                    const mine = (address && m.addr && address.toLowerCase() === String(m.addr).toLowerCase()) || m.from === handle;
                    const text = m.text || m.body || '';
                    return (
                      <Box key={`${m.ts}-${i}`} sx={{ display: 'flex', flexDirection: 'column', alignItems: mine ? 'flex-end' : 'flex-start' }}>
                        <Typography variant="caption" sx={{ color: 'text.secondary', px: 0.5, mb: 0.25 }}>
                          {m.from}
                          <span style={{ opacity: 0.6 }}> • {new Date(m.ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                        </Typography>
                        <Bubble mine={mine} title={new Date(m.ts).toISOString()}>
                          {splitLinks(text).map((p, idx) =>
                            p.link ? (
                              <a key={idx} href={p.t} target="_blank" rel="noreferrer" style={{ color: 'var(--color-primary)', textDecoration: 'underline' }}>
                                {p.t}
                              </a>
                            ) : (
                              <span key={idx}>
                                {p.t.split(handle).reduce((acc, part, i) => {
                                  if (i === 0) return [part];
                                  return acc.concat([<strong key={`hl-${i}`}>{handle}</strong>, part]);
                                }, [])}
                              </span>
                            )
                          )}
                        </Bubble>
                      </Box>
                    );
                  })}
                </Messages>
                {pending > 0 && (
                  <Box sx={{ position: 'absolute', bottom: 56, left: 0, right: 0, display: 'flex', justifyContent: 'center' }}>
                    <Button
                      size="small"
                      onClick={() => {
                        const el = scrollRef.current; if (!el) return;
                        el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
                        setPending(0); setAtBottom(true);
                      }}
                      sx={{
                        borderRadius: 999,
                        px: 1.25,
                        minHeight: 28,
                        background: 'rgba(0,0,0,0.4)',
                        border: '1px solid rgba(255,255,255,0.18)',
                        backdropFilter: 'blur(8px)',
                        textTransform: 'none',
                        fontWeight: 800,
                      }}
                    >
                      ▼ {pending} new message{pending > 1 ? 's' : ''}
                    </Button>
                  </Box>
                )}
              </Box>

              {/* Input */}
              <Divider sx={{ opacity: 0.12 }} />
              <InputBar>
                <TextField
                  fullWidth
                  size="small"
                  placeholder={`Message as ${handle} · Enter to send`}
                  value={input}
                  onChange={(e) => {
                    const v = e.target.value.slice(0, MAX_MESSAGE_LEN);
                    setInput(v);
                    try { localStorage.setItem(draftKey, v); } catch {}
                  }}
                  onKeyDown={handleKeyDown}
                  inputProps={{ maxLength: MAX_MESSAGE_LEN }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      background: 'rgba(255,255,255,0.05)',
                      borderRadius: 'var(--radius-button, 10px)',
                      fontWeight: 600,
                    },
                  }}
                  aria-label="Chat message input"
                  inputRef={inputRef}
                />
                <Tooltip title="Emojis" arrow>
                  <IconButton
                    size="small"
                    onClick={(e) => setEmojiAnchor(e.currentTarget)}
                    sx={{ border: '1px solid rgba(255,255,255,0.14)' }}
                    aria-label="Open emoji picker"
                  >
                    <EmojiEmotionsRoundedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Popover
                  open={!!emojiAnchor}
                  anchorEl={emojiAnchor}
                  onClose={() => setEmojiAnchor(null)}
                  anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                  transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                  PaperProps={{
                    sx: {
                      p: 1,
                      borderRadius: 2,
                      bgcolor: 'rgba(10,12,16,0.9)',
                      border: '1px solid rgba(255,255,255,0.14)'
                    }
                  }}
                >
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(8, 1fr)', gap: 0.5 }}>
                    {EMOJIS.map((emo) => (
                      <Button
                        key={emo}
                        onClick={() => {
                          setEmojiAnchor(null);
                          insertAtCursor(inputRef.current, emo);
                        }}
                        sx={{ minWidth: 0, p: 0.5, fontSize: 18, lineHeight: 1 }}
                        title={emo}
                      >
                        {emo}
                      </Button>
                    ))}
                  </Box>
                </Popover>
                <Button
                  component={motion.button}
                  whileTap={prefersReducedMotion ? undefined : { scale: 0.98 }}
                  whileHover={prefersReducedMotion ? undefined : { scale: 1.02 }}
                  title="Send (Enter)"
                  variant="contained"
                  onClick={doSend}
                  disabled={!canSend}
                  startIcon={<SendRoundedIcon />}
                  sx={{
                    borderRadius: 'var(--radius-button, 10px)',
                    fontWeight: 900,
                    textTransform: 'none',
                    backgroundImage: 'var(--grad-primary)',
                    color: 'var(--button-label-primary, #FFF)',
                    px: 1.5,
                  }}
                >
                  Send
                </Button>
              </InputBar>
            </Panel>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
